import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SIM_CMDS, SIM_EVENTS, ACTION_RE, sanitizeMetrics } from '../lib/simFrame';

const LOAD_TIMEOUT = 10000;
const newNonce = () => (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`).replace(/[^a-zA-Z0-9-]/g, '');

// Accepts messages only from our own iframe window carrying this run's nonce; sends only whitelisted commands.
export const useSimBridge = (frameRef, runKey, declaredActions = []) => {
  const nonce = useMemo(() => newNonce(), [runKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const [status, setStatus] = useState('loading');
  const [supports, setSupports] = useState([]);
  const [actions, setActions] = useState([]);
  const [metrics, setMetrics] = useState([]);
  const [error, setError] = useState('');
  const [lastResult, setLastResult] = useState(null);
  const statusRef = useRef('loading');

  useEffect(() => {
    statusRef.current = 'loading';
    setStatus('loading'); setSupports([]); setActions([]); setMetrics([]); setError(''); setLastResult(null);
    const onMsg = (e) => {
      const win = frameRef.current?.contentWindow;
      if (!win || e.source !== win) return;
      const d = e.data;
      if (!d || d.__amn !== 1 || d.nonce !== nonce || !SIM_EVENTS.includes(d.type)) return;
      const p = d.payload && typeof d.payload === 'object' ? d.payload : {};
      if (d.type === 'loaded' && statusRef.current === 'loading') { statusRef.current = 'loaded'; setStatus('loaded'); }
      if (d.type === 'ready') {
        statusRef.current = 'ready'; setStatus('ready');
        setSupports((Array.isArray(p.supports) ? p.supports : []).filter(c => SIM_CMDS.includes(c)));
        setActions((Array.isArray(p.actions) ? p.actions : []).filter(a => typeof a === 'string' && ACTION_RE.test(a)).slice(0, 50));
      }
      if (d.type === 'metrics') setMetrics(sanitizeMetrics(d.payload));
      if (d.type === 'result') setLastResult(p);
      if (d.type === 'error') setError(String(p.message || '').slice(0, 160));
    };
    window.addEventListener('message', onMsg);
    const t = setTimeout(() => { if (statusRef.current === 'loading') { statusRef.current = 'timeout'; setStatus('timeout'); } }, LOAD_TIMEOUT);
    return () => { window.removeEventListener('message', onMsg); clearTimeout(t); };
  }, [frameRef, nonce]);

  const send = useCallback((type, payload = {}) => {
    if (!SIM_CMDS.includes(type)) return false;
    const win = frameRef.current?.contentWindow;
    if (!win) return false;
    win.postMessage({ __amn: 1, nonce, type, payload }, '*');
    return true;
  }, [frameRef, nonce]);

  const can = useCallback((cmd) => status === 'ready' && supports.includes(cmd), [status, supports]);

  // An action is sent only if the running simulation (or its saved data) declares it.
  const canAction = useCallback((step) => {
    if (!step || !ACTION_RE.test(step) || status !== 'ready') return false;
    const list = actions.length ? actions : declaredActions;
    return list.includes(step);
  }, [actions, declaredActions, status]);

  return { nonce, status, supports, actions, metrics, error, lastResult, send, can, canAction };
};
